import { useCallback, useEffect, useRef, useState } from "react";
import { uploadImage } from "../lib/api";
import { imagePositionStyle } from "../lib/imagePosition";
import type { ImagePosition } from "../lib/types";
import { Button } from "./ui/button";

interface ImagePositionEditorProps {
  imageUrl: string;
  title: string;
  initial: ImagePosition;
  onSave: (position: ImagePosition) => void;
  onCancel: () => void;
  onImageUpload?: (imageUrl: string) => void;
}

const MIN_SCALE = 1;
const MAX_SCALE = 3;
const SCROLL_SENSITIVITY = 0.002;
const MIN_IMAGE_SIZE = 400;

function clampOffset(offset: number): number {
  return Math.max(-50, Math.min(50, offset));
}

export function ImagePositionEditor({
  imageUrl,
  title,
  initial,
  onSave,
  onCancel,
  onImageUpload,
}: ImagePositionEditorProps) {
  const [currentImageUrl, setCurrentImageUrl] = useState(imageUrl);
  const [scale, setScale] = useState(initial.imageScale);
  const [offsetX, setOffsetX] = useState(initial.imageOffsetX);
  const [offsetY, setOffsetY] = useState(initial.imageOffsetY);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const dragging = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleWheel = useCallback(
    (e: WheelEvent) => {
      e.preventDefault();
      setScale((prev) =>
        Math.max(MIN_SCALE, Math.min(MAX_SCALE, prev - e.deltaY * SCROLL_SENSITIVITY)),
      );
    },
    [],
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [handleWheel]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    dragging.current = true;
    lastPos.current = { x: e.clientX, y: e.clientY };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging.current) return;
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const dx = ((e.clientX - lastPos.current.x) / rect.width) * 100;
      const dy = ((e.clientY - lastPos.current.y) / rect.height) * 100;
      lastPos.current = { x: e.clientX, y: e.clientY };

      setOffsetX((prev) => clampOffset(prev + dx / scale));
      setOffsetY((prev) => clampOffset(prev + dy / scale));
    },
    [scale],
  );

  const handlePointerUp = useCallback(() => {
    dragging.current = false;
  }, []);

  const handleSave = useCallback(() => {
    onSave({ imageScale: scale, imageOffsetX: offsetX, imageOffsetY: offsetY });
  }, [scale, offsetX, offsetY, onSave]);

  const handleReset = useCallback(() => {
    setScale(1);
    setOffsetX(0);
    setOffsetY(0);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        onCancel();
      } else if (e.key === "Enter") {
        handleSave();
      }
    },
    [onCancel, handleSave],
  );

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!fileInputRef.current) return;
      fileInputRef.current.value = "";
      if (!file) return;

      setUploadError(null);

      let bitmap: ImageBitmap;
      try {
        bitmap = await createImageBitmap(file);
      } catch {
        setUploadError("Could not read the image. Please upload a valid PNG or JPEG file.");
        return;
      }

      const { width, height } = bitmap;
      bitmap.close();

      if (width < MIN_IMAGE_SIZE || height < MIN_IMAGE_SIZE) {
        setUploadError(
          `Image is too small (${width}×${height}px). Please upload an image that is at least ${MIN_IMAGE_SIZE}×${MIN_IMAGE_SIZE}px.`,
        );
        return;
      }

      setUploading(true);
      const result = await uploadImage(file);
      setUploading(false);

      if (!result.ok) {
        setUploadError(result.error);
        return;
      }

      setCurrentImageUrl(result.data.url);
      setScale(1);
      setOffsetX(0);
      setOffsetY(0);
      onImageUpload?.(result.data.url);
    },
    [onImageUpload],
  );

  return (
    <div
      className="grid gap-3"
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div
        ref={containerRef}
        className="product-image-bg w-full aspect-[4/3] overflow-hidden relative rounded-t-[14px] cursor-grab active:cursor-grabbing ring-2 ring-accent/60"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        data-testid="image-position-editor-viewport"
      >
        <img
          className="w-full h-full object-cover select-none"
          src={currentImageUrl}
          alt={title}
          draggable={false}
          style={imagePositionStyle(scale, offsetX, offsetY)}
        />
      </div>

      {uploadError && (
        <p
          data-testid="upload-error"
          className="text-red-400 text-[0.85rem] px-1 m-0"
        >
          {uploadError}
        </p>
      )}

      <div className="flex items-center gap-3 px-3">
        <label className="text-[0.8rem] text-muted shrink-0 w-12">Zoom</label>
        <input
          type="range"
          min={MIN_SCALE}
          max={MAX_SCALE}
          step={0.01}
          value={scale}
          onChange={(e) => {
            setScale(parseFloat(e.target.value));
          }}
          className="flex-1 accent-accent h-1"
        />
        <span className="text-[0.8rem] text-muted w-10 text-right tabular-nums">
          {Math.round(scale * 100)}%
        </span>
      </div>

      <div className="flex gap-2 px-3">
        <Button
          className="text-xs flex-1"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          className="text-xs"
          onClick={handleReset}
        >
          Reset
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg"
          className="hidden"
          onChange={handleFileChange}
          data-testid="image-file-input"
        />
        <Button
          className="text-xs"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? "Uploading…" : "Upload image"}
        </Button>
        <Button
          variant="primary"
          className="text-xs flex-1"
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
